/**
 * Pitṛ-doṣa identification — deterministic, classically-grounded.
 *
 * Rule-based check across 8 classical signatures. Karma-level guidance
 * only; serious cases explicitly recommend consulting a qualified
 * astrologer or purohita. No mantra prescription.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest, PitraDoshaResponse } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

const SEVERITY_PALETTE: Record<string, { bg: string; ring: string; text: string }> = {
  none: { bg: 'bg-emerald-950/40', ring: 'ring-emerald-800', text: 'text-emerald-200' },
  mild: { bg: 'bg-amber-950/40', ring: 'ring-amber-800', text: 'text-amber-200' },
  moderate: { bg: 'bg-orange-950/40', ring: 'ring-orange-800', text: 'text-orange-200' },
  strong: { bg: 'bg-red-950/50', ring: 'ring-red-800', text: 'text-red-200' },
};

export function PitraDoshaPage() {
  const { lang, t } = useLang();
  const [report, setReport] = useState<PitraDoshaResponse | null>(null);

  const mutation = useMutation<PitraDoshaResponse, ApiError, ChartCreateRequest>({
    mutationFn: (req) => api.pitraDosha(req),
    onSuccess: setReport,
  });

  const handleSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setReport(null);
      mutation.mutate(req);
    },
    [mutation],
  );

  return (
    <article className="mx-auto max-w-3xl space-y-6 px-4 py-8 text-stone-100">
      <header>
        <h1 className="text-2xl font-bold text-saffron-200">
          {t('पितृ-दोष विश्लेषण', 'Pitṛ-doṣa Analysis')}
        </h1>
        <p className="mt-1 text-sm text-stone-400">
          {t(
            'आठ शास्त्रीय संकेतों का नियम-आधारित परीक्षण। केवल कर्म-स्तरीय निर्देश; कोई मन्त्र-प्रिस्क्रिप्शन नहीं।',
            'Rule-based check across eight classical signatures. Karma-level guidance only; no mantra prescription.',
          )}
        </p>
      </header>

      <DisclaimerBanner lang={lang} />

      <ChartInputForm onSubmit={handleSubmit} isSubmitting={mutation.isPending} />

      {mutation.isError && (
        <div className="rounded-2xl bg-red-950/40 p-4 ring-1 ring-red-800">
          <p className="text-sm text-red-200">{mutation.error?.detail ?? t('त्रुटि', 'Error')}</p>
        </div>
      )}

      {mutation.isPending && (
        <p className="text-sm italic text-stone-500">{t('गणना हो रही है…', 'Computing…')}</p>
      )}

      {report && <PitraDoshaReport report={report} lang={lang} />}
    </article>
  );
}

function DisclaimerBanner({ lang }: { lang: 'hi' | 'en' }) {
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  return (
    <div className="rounded-2xl bg-amber-950/30 p-3 ring-1 ring-amber-800/50">
      <p className="text-xs leading-relaxed text-amber-100">
        {t(
          'यह विश्लेषण शास्त्रीय नियम-आधारित है — सूचनार्थ। यह निदान अथवा अन्तिम-निर्णय नहीं। यदि गम्भीर संकेत मिलें, तो किसी योग्य ज्योतिषी अथवा पुरोहित से व्यक्तिगत परामर्श करें — आत्म-निर्णय से उपाय न करें।',
          'This analysis is classical rule-based and informational. It is not a diagnosis or final verdict. If serious indicators appear, consult a qualified astrologer or purohita individually — do not self-prescribe remedies.',
        )}
      </p>
    </div>
  );
}

function PitraDoshaReport({ report, lang }: { report: PitraDoshaResponse; lang: 'hi' | 'en' }) {
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const palette = SEVERITY_PALETTE[report.severity];

  return (
    <div className="space-y-5">
      {/* Status card */}
      <section className={`rounded-2xl ${palette.bg} p-5 ring-1 ${palette.ring}`}>
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-400">
            {t('स्थिति', 'Status')}
          </h2>
          <span className={`rounded-full px-3 py-0.5 text-xs font-semibold uppercase tracking-wider ${palette.text} ring-1 ${palette.ring}`}>
            {report.severity}
          </span>
        </div>
        <p className={`mt-2 text-base font-semibold ${palette.text}`}>
          {lang === 'hi' ? report.severity_label_hi : report.severity_label_en}
          {report.is_present && (
            <>
              {' '}
              <span className="text-sm font-normal text-stone-400">
                ({report.signature_count} {t('संकेत', 'signature(s)')})
              </span>
            </>
          )}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-stone-200">
          {lang === 'hi' ? report.summary_hi : report.summary_en}
        </p>
      </section>

      {/* Signatures (if any) */}
      {report.signatures.length > 0 && (
        <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
          <h2 className="mb-3 text-base font-semibold text-saffron-200">
            {t('प्राप्त शास्त्रीय संकेत', 'Classical Signatures Found')}
          </h2>
          <div className="space-y-3">
            {report.signatures.map((s) => (
              <div
                key={s.rule_id}
                className="rounded-md border-l-2 border-red-700/60 bg-stone-900/50 px-3 py-2.5"
              >
                <p className="text-sm font-semibold text-red-200">
                  {lang === 'hi' ? s.rule_hi : s.rule_en}
                </p>
                <p className="mt-1 text-sm text-stone-100">{lang === 'hi' ? s.fired_by_hi : s.fired_by_en}</p>
                <p className="mt-1 text-xs italic text-stone-500">
                  {t('स्रोत:', 'Source:')} {lang === 'hi' ? s.source_hi : s.source_en}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Karma-level guidance */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('कर्म-स्तरीय निर्देश', 'Karma-Level Guidance')}
        </h2>
        <p className="text-sm leading-relaxed text-stone-200">
          {lang === 'hi' ? report.guidance_hi : report.guidance_en}
        </p>
      </section>

      {/* Astrologer recommendation banner */}
      {report.consult_astrologer && (
        <section className="rounded-2xl border-l-4 border-red-600 bg-red-950/30 p-5 ring-1 ring-red-900/50">
          <h2 className="mb-2 text-base font-bold text-red-200">
            {t('ज्योतिषी-परामर्श अनिवार्य', 'Astrologer Consultation Required')}
          </h2>
          <p className="text-sm leading-relaxed text-red-100">
            {t(
              'संकेतों की संख्या को देखते हुए, कोई भी विशेष कर्म-काण्ड (तीर्थ-श्राद्ध, नारायण-नाग-बलि आदि) करने से पूर्व योग्य ज्योतिषी अथवा पुरोहित से व्यक्तिगत-रूप से कुण्डली का परीक्षण करवाना अनिवार्य है। आत्म-निर्णय से किया गया उपाय न केवल अप्रभावी होता है, अपितु प्रतिकूल भी हो सकता है।',
              'Given the number of indicators, please consult a qualified astrologer or purohita individually before undertaking any special karma-rituals (tīrtha-śrāddha, nārāyaṇa-nāga-bali, etc.). Self-prescribed remedies are not only ineffective but can be counterproductive.',
            )}
          </p>
        </section>
      )}

      {/* Methodology */}
      <section className="rounded-2xl bg-stone-900/30 p-3 ring-1 ring-stone-800/50">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
          {t('विधि-सूचना', 'Methodology')}
        </h3>
        <ul className="space-y-1 text-[10px] leading-snug text-stone-500">
          {report.methodology.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">{lang === 'hi' ? m.method_hi : m.method_en}</span>
              {' — '}
              <em>{lang === 'hi' ? m.source_hi : m.source_en}</em>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[9px] text-stone-700">{report.rules_version}</p>
      </section>
    </div>
  );
}
